import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { IEnabledTest } from '../../models/teamTestConfig';
import { Athlete, TimeframeKey, timeframeLabels, athleteColors } from './testingTypes';
import { useTeamAnalytics } from '../hooks/useTeamAnalytics';
import TeamBoxPlot from './TeamBoxPlot';
import PercentileHeatmap from './PercentileHeatmap';
import RoleComparisonChart from './RoleComparisonChart';
import CrossTestScatter from './CrossTestScatter';
import styles from './TestingAnalyticsView.module.scss';

type AnalyticsView = 'temporale' | 'confronto' | 'percentili' | 'ruoli' | 'relazioni';

// Vista relazioni aggiunta (sez.8.3)
const ANALYTICS_VIEWS: { key: AnalyticsView; label: string }[] = [
  { key: 'temporale', label: 'Andamento squadra' },
  { key: 'confronto', label: 'Confronto (box plot)' },
  { key: 'percentili', label: 'Percentili' },
  { key: 'ruoli', label: 'Per ruolo' },
  { key: 'relazioni', label: 'Relazioni' },
];

interface TestingAnalyticsViewProps {
  categoryTests: ITestDefinition[];
  athletes: Athlete[];
  historyMap: Record<string, ITestResult[]>;
  /** Config team: necessaria per propagare label personalizzate ai sotto-componenti */
  enabledTests?: IEnabledTest[];
}

export default function TestingAnalyticsView({ categoryTests, athletes, historyMap, enabledTests }: TestingAnalyticsViewProps) {
  const [chartTimeframes, setChartTimeframes] = useState<Record<string, TimeframeKey>>({});
  const [analyticsView, setAnalyticsView] = useState<AnalyticsView>('temporale');
  const [globalTimeframe, setGlobalTimeframe] = useState<TimeframeKey>('3m');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  /**
   * Metriche secondarie selezionabili per test multi-metrica (sez.8.1/8.3).
   * Chiave: testId → metrica selezionata (default = primaryMetric o 'value' scalare)
   */
  const [selectedMetrics, setSelectedMetrics] = useState<Record<string, string>>({});

  // Analitiche complementari (spec sez.8.2): confronto squadra (box plot), percentili
  // direction-aware, evoluzione per ruolo. Percentili/box usano la direzione di merito.
  const { percentileCells, boxPlotStats, roleSeriesMap, allRoles } = useTeamAnalytics(
    categoryTests,
    athletes,
    historyMap,
    globalTimeframe,
    selectedRoles,
  );

  const handleRoleToggle = (role: string) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );

  const getDateRange = (testId: string) => {
    const timeframe = chartTimeframes[testId] || '3m';
    const today = new Date();
    let startDate = new Date(today);

    switch (timeframe) {
      case '1m':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case '3m':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case '6m':
        startDate.setMonth(today.getMonth() - 6);
        break;
      case 'season':
        startDate = new Date(today.getFullYear(), 8, 1);
        if (today.getMonth() < 8) {
          startDate.setFullYear(today.getFullYear() - 1);
        }
        break;
      case 'all':
        startDate = new Date('2020-01-01');
        break;
    }

    return { startDate: startDate.toISOString().split('T')[0], endDate: today.toISOString().split('T')[0] };
  };

  /**
   * Risolve il valore da plottare per un risultato.
   * Se il test ha outputMetrics (multi-metrica) e l'utente ha selezionato una metrica
   * secondaria, legge r.values[metricKey]; altrimenti usa r.value scalare (sez.8.1).
   */
  const resolveValue = (r: ITestResult, test: ITestDefinition): number => {
    const metricKey = selectedMetrics[test.id];
    if (metricKey && metricKey !== '__primary__' && r.values && r.values[metricKey] !== undefined) {
      return r.values[metricKey];
    }
    return r.value;
  };

  return (
    <div className={styles.analyticsStack}>
      {/* Switcher viste analitiche (sez.8): principale temporale + complementari */}
      <div className={styles.timeframePills}>
        {ANALYTICS_VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => setAnalyticsView(v.key)}
            className={[styles.timeframePill, analyticsView === v.key ? styles.timeframePillActive : ''].filter(Boolean).join(' ')}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Timeframe per le viste complementari (le temporali hanno il proprio per-test) */}
      {analyticsView !== 'temporale' && analyticsView !== 'relazioni' && (
        <div className={styles.timeframePills}>
          {(Object.keys(timeframeLabels) as TimeframeKey[]).map((tf) => (
            <button
              key={tf}
              onClick={() => setGlobalTimeframe(tf)}
              className={[styles.timeframePill, globalTimeframe === tf ? styles.timeframePillActive : ''].filter(Boolean).join(' ')}
            >
              {timeframeLabels[tf]}
            </button>
          ))}
        </div>
      )}

      {analyticsView === 'confronto' && (
        <TeamBoxPlot categoryTests={categoryTests} boxPlotStats={boxPlotStats} enabledTests={enabledTests} />
      )}
      {analyticsView === 'percentili' && (
        <PercentileHeatmap
          categoryTests={categoryTests}
          athletes={athletes}
          cells={percentileCells}
        />
      )}
      {analyticsView === 'ruoli' && (
        <RoleComparisonChart
          categoryTests={categoryTests}
          roleSeriesMap={roleSeriesMap}
          allRoles={allRoles}
          selectedRoles={selectedRoles}
          onRoleToggle={handleRoleToggle}
        />
      )}

      {/* Vista relazioni cross-test (sez.8.3) — il timeframe è gestito internamente */}
      {analyticsView === 'relazioni' && (
        <CrossTestScatter
          categoryTests={categoryTests}
          athletes={athletes}
          historyMap={historyMap}
          globalTimeframe={globalTimeframe}
          onTimeframeChange={setGlobalTimeframe}
        />
      )}

      {analyticsView === 'temporale' && categoryTests.map((test: ITestDefinition) => {
        const { startDate, endDate } = getDateRange(test.id);
        const athleteData: Record<string, { date: string; value: number }[]> = {};

        // Metriche disponibili per selettore sotto-metrica (sez.8.1/8.3)
        const availableMetrics: { key: string; label: string }[] = [];
        if (test.primaryMetric) {
          availableMetrics.push({ key: '__primary__', label: `${test.primaryMetric} (primaria)` });
        }
        if (test.outputMetrics && test.outputMetrics.length > 0) {
          test.outputMetrics
            .filter((m) => m !== test.primaryMetric)
            .forEach((m) => availableMetrics.push({ key: m, label: m }));
        }
        const isMultiMetric = availableMetrics.length > 1;
        const currentMetric = selectedMetrics[test.id] ?? '__primary__';

        athletes.forEach((athlete) => {
          const history = historyMap[`${athlete.id}_${test.id}`] ?? [];
          const filtered = history
            .filter((r) => r.value > 0 && r.date >= startDate && r.date <= endDate)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          if (filtered.length > 0) {
            athleteData[athlete.id] = filtered.map((r) => ({
              date: r.date,
              value: resolveValue(r, test),
            }));
          }
        });

        if (Object.keys(athleteData).length === 0) return null;

        const allDates = new Set<string>();
        Object.values(athleteData).forEach((data) => {
          data.forEach((point) => allDates.add(point.date));
        });

        const sortedDates = Array.from(allDates).sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );

        const chartData = sortedDates.map((date) => {
          const point: any = {
            date: new Date(date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: '2-digit' }),
          };

          athletes.forEach((athlete) => {
            const athletePoint = athleteData[athlete.id]?.find((d) => d.date === date);
            if (athletePoint) {
              point[athlete.id] = athletePoint.value;
            }
          });

          return point;
        });

        return (
          <div key={test.id} className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <div>
                <h3 className={styles.chartTitle}>{test.name}</h3>
                <p className={styles.chartSubtitle}>Comparazione team</p>
              </div>

              <div className={styles.chartHeaderControls}>
                {/* Selettore sotto-metrica per test multi-metrica (sez.8.1) */}
                {isMultiMetric && (
                  <div className={styles.metricSelector}>
                    <label className={styles.metricSelectorLabel}>Metrica</label>
                    <select
                      className={styles.metricSelect}
                      value={currentMetric}
                      onChange={(e) =>
                        setSelectedMetrics((prev) => ({ ...prev, [test.id]: e.target.value }))
                      }
                    >
                      {availableMetrics.map((m) => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={styles.timeframePills}>
                  {(Object.keys(timeframeLabels) as TimeframeKey[]).map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setChartTimeframes((prev) => ({ ...prev, [test.id]: tf }))}
                      className={[styles.timeframePill, (chartTimeframes[test.id] || '3m') === tf ? styles.timeframePillActive : ''].filter(Boolean).join(' ')}
                    >
                      {timeframeLabels[tf]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {chartData.length > 0 && (
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--c-card-bg)',
                        border: '1px solid #e7e7e7',
                        borderRadius: '8px',
                        fontFamily: "'Satoshi:Medium',sans-serif",
                        fontSize: '12px',
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontFamily: "'Satoshi:Medium',sans-serif",
                        fontSize: '12px',
                      }}
                      formatter={(value) => {
                        const athlete = athletes.find((a) => a.id === value);
                        return athlete ? athlete.name : value;
                      }}
                    />
                    {athletes.map((athlete, idx) => {
                      if (!athleteData[athlete.id]) return null;
                      const colorKeys = Object.keys(athleteColors);
                      const color = athleteColors[String(idx + 1)] ?? athleteColors[colorKeys[idx % colorKeys.length]] ?? '#013344';

                      return (
                        <Line
                          key={athlete.id}
                          type="monotone"
                          dataKey={athlete.id}
                          name={athlete.name}
                          stroke={color}
                          strokeWidth={2}
                          dot={{ fill: color, r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
